import React from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';

const navItems = [
  { to: '/lite', label: 'Home', icon: IconHome },
  { to: '/lite/preorders', label: 'Pre-ord', icon: IconClipboard },
  { to: '/lite/sales', label: 'Sales', icon: IconCart },
  { to: '/lite/products', label: 'Products', icon: IconSearch },
  { to: '/lite/more', label: 'More', icon: IconDots },
];

function IconHome({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z" />
    </svg>
  );
}

function IconClipboard({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      <line x1="9" y1="5" x2="9" y2="7" />
      <line x1="7" y1="9" x2="15" y2="9" />
    </svg>
  );
}

function IconCart({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}

function IconSearch({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconDots({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

function LitePlaceholder({ title, description, children, desktopPath, extra }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-emerald-300">Lite preview</p>
          <h1 className="text-2xl font-semibold text-white">{title}</h1>
        </div>
        <span className="rounded-full border border-amber-400/60 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
          Coming soon
        </span>
      </div>
      <p className="text-sm text-slate-300">{description}</p>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-200 shadow-inner shadow-black/30">
        <h3 className="text-base font-semibold text-white">Planned for Lite</h3>
        <div className="mt-2 space-y-2 text-slate-300">{children}</div>
      </div>

      {desktopPath && (
        <Link
          to={desktopPath}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:border-emerald-400"
        >
          Open desktop view
          <span aria-hidden="true">↗</span>
        </Link>
      )}
      {extra}
    </div>
  );
}

export default function PosLiteShell() {
  const location = useLocation();
  const currentTitle =
    navItems.reduce((label, item) => {
      const matches =
        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
      return matches ? item.label : label;
    }, 'POS Lite');

  return (
    <div className="relative flex min-h-screen flex-col bg-slate-950 text-white">
      <header className="sticky top-0 z-20 border-b border-slate-900/80 bg-slate-950/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600/15 text-emerald-200">
              <span className="text-lg font-black">O</span>
            </div>
            <div className="leading-tight">
              <p className="text-xs uppercase tracking-[0.25em] text-emerald-300">POS Lite</p>
              <p className="text-lg font-semibold text-white">{currentTitle}</p>
            </div>
          </div>
          <div className="rounded-full border border-emerald-500/40 bg-emerald-600/15 px-3 py-1 text-xs font-semibold text-emerald-200">
            Mobile
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-24 pt-4">
        <Outlet />
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-900/80 bg-slate-950/95 py-2 shadow-[0_-10px_35px_rgba(0,0,0,0.45)] backdrop-blur">
        <div className="mx-auto grid max-w-xl grid-cols-5 gap-1 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/lite'}
                className={({ isActive }) =>
                  [
                    'flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-xs font-semibold transition',
                    isActive
                      ? 'bg-emerald-600/15 text-emerald-200 border border-emerald-500/40 shadow-[0_10px_30px_-15px_rgba(16,185,129,0.65)]'
                      : 'text-slate-400 hover:text-emerald-200 hover:bg-slate-900/70',
                  ].join(' ')
                }
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export function LitePreordersPlaceholder() {
  return (
    <LitePlaceholder
      title="Pre-orders"
      description="Mobile cards will show customer, status, totals, and quick actions to send invoice/receipt/pickup."
      desktopPath="/preorders"
    >
      <ul className="list-disc space-y-1 pl-5">
        <li>Card list with status badges and created timestamps.</li>
        <li>Detail view with items, contact, totals, and status updates.</li>
        <li>Full-width buttons to trigger invoice, receipt + pickup time, and pickup notification.</li>
      </ul>
    </LitePlaceholder>
  );
}

export function LiteSalesPlaceholder() {
  return (
    <LitePlaceholder
      title="Sales Lite"
      description="A thumb-friendly version of Sales with search, add-to-cart, and quick checkout."
      desktopPath="/"
    >
      <ul className="list-disc space-y-1 pl-5">
        <li>Sticky search at the top; product cards with large add buttons.</li>
        <li>Sticky cart footer with total and checkout.</li>
        <li>Simple payment confirmation using existing receipt endpoints.</li>
      </ul>
    </LitePlaceholder>
  );
}

export function LiteProductsPlaceholder() {
  return (
    <LitePlaceholder
      title="Product Lookup"
      description="Fast product search showing price and stock in a single-column list."
      desktopPath="/products"
    >
      <ul className="list-disc space-y-1 pl-5">
        <li>Search-first layout with big tap targets.</li>
        <li>One-column cards with name, price, and stock.</li>
        <li>Offline-friendly caching once the service worker ships.</li>
      </ul>
    </LitePlaceholder>
  );
}

export function LiteMorePlaceholder() {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.assign('/login');
  };

  return (
    <LitePlaceholder
      title="More"
      description="Quick links to desktop views plus Lite settings and PWA install guidance."
      desktopPath="/settings"
      extra={
        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-200 space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Session</p>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400"
          >
            Logout / Go to login
          </button>
          <p className="text-xs text-slate-500">Use this if your session expires on Lite.</p>
        </div>
      }
    >
      <ul className="list-disc space-y-1 pl-5">
        <li>Links to desktop POS for advanced tasks.</li>
        <li>Usage tips and install-to-home-screen steps.</li>
        <li>Support/help shortcuts.</li>
      </ul>
    </LitePlaceholder>
  );
}

